import React, { Fragment, useState, useEffect } from 'react';
import { PAGES, ACTIONS, usePermissions } from 'hooks/permissions/permissions';
import StatusBadge from 'components/statusBadge';
import { Row, Col, Button, Input, Pagination, message, Popconfirm, Tag } from 'antd';
import { QuestionCircleOutlined, SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { useShiftList, useDeleteShift, useShiftStatus } from "hooks/configuration/shift/shift";
import { Link } from 'react-router-dom';
import { statusColor, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from "helpers/commonFunctions";
import Image from 'helpers/Image';
import "../style.css";
import TableStateRow from "components/tableState";

/** "9 h", "8 h 30 m". */
export const formatDuration = (minutes) => {
    const h = Math.floor((minutes || 0) / 60);
    const m = (minutes || 0) % 60;
    return m ? `${h} h ${m} m` : `${h} h`;
};

const Shift = () => {

    const { can } = usePermissions();
    const canAdd = can(PAGES.SHIFT, ACTIONS.ADD);
    const canEdit = can(PAGES.SHIFT, ACTIONS.EDIT);
    const canDelete = can(PAGES.SHIFT, ACTIONS.DELETE);
    const showActions = canEdit || canDelete;
    const columnCount = showActions ? 7 : 6;

    const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
    const [listData, setListData] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [defaultCall, setDefaultCall] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [sortField, setSortField] = useState("");
    const [sortMethod, setSortMethod] = useState("asc");
    const [initValues, setInitValues] = useState({
        "pageNumber": pageNumber,
        "pageSize": page,
        "searchKey": searchKey,
        "sortField": sortField,
        "sortMethod": sortMethod
    });

    const [sorting, setSorting] = useState({
        "s.shift_name": null,
        "s.start_time": null,
        "s.end_time": null,
        "employee_count": null,
        "s.active": null
    });

    const { mutate: shiftList, data: shiftListData, isLoading: Loading } = useShiftList();
    const { mutate: deleteShift, isLoading: deleteLoading } = useDeleteShift();
    const { mutate: statusChange } = useShiftStatus();

    const onSortChange = (column) => {
        initValues.pageNumber = 0;
        initValues.sortField = column;
        initValues.sortMethod = sorting[column] === 'asc' ? 'desc' : 'asc';
        setPageNumber(0);
        const newSorting = {
            ...sorting,
            [column]: sorting[column] === 'asc' ? 'desc' : 'asc',
        };
        setSorting(newSorting);
        setInitValues(initValues);
        setSortField(column);
        setSortMethod(newSorting[column]);
        setListData([]);
    };

    const sortIcon = (column) => sorting[column] === 'asc'
        ? <SortAscendingOutlined onClick={() => onSortChange(column)} />
        : <SortDescendingOutlined onClick={() => onSortChange(column)} />;

    const onFetchShiftList = (values) => {
        shiftList(values, {
            onSuccess: (item) => {
                setListData(item?.data?.content);
            }
        })
    }

    // A refused delete (employees still assigned) comes back as a 400; the shared API client
    // shows its message, so only a success needs handling here.
    const deleteId = (id) => {
        deleteShift(id, {
            onSuccess: (item) => {
                if (item?.statusCode === 200) {
                    message.success(item.message);
                    onFetchShiftList(initValues);
                }
            },
        });
    }

    const handleConfirmActive = (id) => {
        statusChange(id, {
            onSuccess: (item) => {
                if (item?.statusCode === 200) {
                    message.success(item.message, 6);
                    onFetchShiftList(initValues);
                }
            },
        });
    }

    const onChange = (page) => {
        initValues.pageNumber = page - 1;
        setInitValues(initValues);
        setPageNumber(page - 1);
    }

    const onShowSizeChange = (current, pageSize) => {
        setPage(initValues.pageSize = pageSize)
    }

    const onSearchHandler = (e) => {
        initValues.searchKey = e.target.value;
        let data = { ...initValues };
        data.searchKey = e.target.value;
        data.pageNumber = e.target.value !== "" ? 0 : pageNumber;
        setInitValues(data);
        setSearchKey(e.target.value);
    }

    const statusQuestion = (item) => {
        if (!item?.status)
            return "Activate this shift?";
        return item?.employeeCount > 0
            ? `Deactivate this shift? Its ${item.employeeCount} employee(s) keep it, but missing check-outs will be closed by the duration cap instead of the shift end.`
            : "Deactivate this shift?";
    };

    useEffect(() => {
        setDefaultCall(true);
    }, [])

    useEffect(() => {
        if (defaultCall) {
            onFetchShiftList(initValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultCall, pageNumber, searchKey, page, sortField, sortMethod]);

    return (
        <Fragment>
            <div className="common-head">
                <div className="title">
                    <h1>Shifts</h1>
                    <p>Working hours assigned to employees; the shift end closes a day with no check-out</p>
                </div>
                <div className="txt-right single-line">
                    <Input placeholder='Search' prefix={<Image className="search-img" name={"search.svg"} />} allowClear onChange={onSearchHandler} />
                    {!canAdd ? null : <Link to="/configuration/shift/create">
                        <Button><Image name="plus.png" /> New Shift</Button>
                    </Link>}
                </div>
            </div>
            <div className="common-table">
                <table>
                    <thead>
                        <tr>
                            <th>Shift Name {sortIcon("s.shift_name")}</th>
                            <th>Start {sortIcon("s.start_time")}</th>
                            <th>End {sortIcon("s.end_time")}</th>
                            <th>Duration</th>
                            <th>Employees {sortIcon("employee_count")}</th>
                            <th>Status {sortIcon("s.active")}</th>
                            {showActions ? <th>Action</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {Loading ? (
                            <TableStateRow colSpan={columnCount} loading />
                        ) : listData == null || listData?.length === 0 ? (
                            <TableStateRow colSpan={columnCount}>No shifts found</TableStateRow>
                        ) : listData.map((item) => (
                            <tr key={item.shiftId}>
                                <td>{item?.shiftName || <span style={{ color: "var(--ui-muted)" }}>Unnamed</span>}</td>
                                <td>{item?.startTime}</td>
                                <td>{item?.endTime}</td>
                                <td>
                                    {formatDuration(item?.durationMinutes)}
                                    {item?.overnight ? <Tag color="purple" style={{ marginLeft: 8 }}>Overnight</Tag> : null}
                                </td>
                                <td>{item?.employeeCount}</td>
                                <td>
                                    {canEdit ? <Popconfirm title={statusQuestion(item)}
                                        onConfirm={() => handleConfirmActive(item.shiftId)}
                                        icon={<QuestionCircleOutlined />}
                                        okText="Yes"
                                        cancelText="No">
                                        <button className={statusColor(item.status)}>
                                            {item?.status ? "Active" : "InActive"}
                                        </button>
                                    </Popconfirm> : <StatusBadge active={item?.status} />}
                                </td>
                                {showActions ? <td>
                                    {!canEdit ? null : <Link to={`/configuration/shift/${item.shiftId}`}>
                                        <Image name="edit.png" />
                                    </Link>}
                                    {!canDelete ? null : <Popconfirm
                                        Loading={deleteLoading}
                                        title={item?.employeeCount > 0
                                            ? `${item.employeeCount} employee(s) are on this shift, so it can't be deleted until they are reassigned. Try anyway?`
                                            : "Delete this shift?"}
                                        onConfirm={() => deleteId(item?.shiftId)}
                                        icon={<QuestionCircleOutlined />}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Image name="delete.png" />
                                    </Popconfirm>}
                                </td> : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Row className="table-pagination">
                <Col md={12}>
                    <h5>Showing {pageNumber === 0 ? 1 : (shiftListData?.data?.pageable?.offset + 1 || 0)} to {shiftListData?.data?.pageable?.offset + shiftListData?.data?.numberOfElements || 0} of {shiftListData?.data?.totalElements} entries</h5>
                </Col>
                <Col md={12} className="txt-right">
                    {shiftListData?.data?.content?.length === 0 ? null : <Pagination pageSize={page || DEFAULT_PAGE_SIZE} showSizeChanger pageSizeOptions={PAGE_SIZE_OPTIONS} onChange={onChange} current={pageNumber + 1} onShowSizeChange={onShowSizeChange} total={shiftListData?.data?.totalElements} />}
                </Col>
            </Row>
        </Fragment >
    )
}

export default Shift;
